import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { reaction, toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { BarChart3 } from "lucide-react";
import { PageHeader } from "@/components/common";
import { useTran } from "@/hooks";
import FilterBar from "../components/FilterBar";
import KpiCards from "../components/KpiCards";
import CategoryProgressChart from "../components/CategoryProgressChart";
import CompletedTargetsChart from "../components/CompletedTargetsChart";
import TargetDataOverviewCard from "../components/TargetDataOverviewCard";
import { loadFilterOptions } from "../operations/load-filter-options.ops";
import { loadKpiSummary } from "../operations/load-kpi-summary.ops";
import { loadTargetDataOverview } from "../operations/load-target-data-overview.ops";
import {
  loadAllMetricDetails,
  loadAllOrgDetails,
} from "../operations/load-target-data-detail.ops";
import { loadConsolidatedMetricOverview } from "../operations/load-consolidated-metric-overview.ops";
import { loadAllConsolidatedMetricDetails } from "../operations/load-consolidated-metric-detail.ops";
import { loadAllConsolidatedOrgDetails } from "../operations/load-consolidated-org-detail.ops";
import { loadEsgDataTable } from "../operations/load-esg-data-table.ops";
import { dashboardFiltersStore } from "../stores/filters.store";
import { dashboardOverviewStore } from "../stores/target-data-overview.store";
import { dashboardMetricDetailStore } from "../stores/metric-detail.store";
import { dashboardOrgDetailStore } from "../stores/org-detail.store";
import { dashboardConsolidatedMetricOverviewStore } from "../stores/consolidated-metric-overview.store";
import { dashboardConsolidatedMetricDetailStore } from "../stores/consolidated-metric-detail.store";
import { dashboardConsolidatedOrgDetailStore } from "../stores/consolidated-org-detail.store";
import { dashboardDataTableStore } from "../stores/data-table.store";
import { dashboardEsgDataTableStore } from "../stores/esg-data-table.store";
import { seedBottomTabFilters } from "../utils/dashboard-seed";
import { useAuthStore } from "@/features/auth";

const FILTER_DEBOUNCE_MS = 500;
const OVERVIEW_DEBOUNCE_MS = 400;
const DETAIL_DEBOUNCE_MS = 400;

const VALID_TABS = new Set(["overview", "esg", "charts"]);

const EsgDashboardPage = observer(() => {
  const { t } = useTran();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();

  useEffect(() => {
    const initial = searchParams.get("tab");
    if (initial && VALID_TABS.has(initial)) {
      dashboardDataTableStore.setTab(initial);
    }
    // intentionally read once on mount — subsequent URL changes flow via setTab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentTab = dashboardDataTableStore.tab;
  useEffect(() => {
    if (searchParams.get("tab") === currentTab) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", currentTab);
    setSearchParams(next, { replace: true });
  }, [currentTab, searchParams, setSearchParams]);

  useEffect(() => {
    loadFilterOptions();
    loadKpiSummary();
    loadTargetDataOverview();

    const disposeKpi = reaction(
      () => ({
        years: toJS(dashboardFiltersStore.filters.years),
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        hierarchy: dashboardFiltersStore.filters.hierarchy,
        orgIds: toJS(dashboardFiltersStore.filters.orgIds),
      }),
      () => loadKpiSummary(),
      { delay: FILTER_DEBOUNCE_MS },
    );

    const isConsolidatedActive = () =>
      dashboardOverviewStore.groupBy === "metric" &&
      dashboardOverviewStore.viewMode === "consolidated";

    const disposeOverview = reaction(
      () => ({
        years: toJS(dashboardFiltersStore.filters.years),
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        categories: toJS(dashboardFiltersStore.filters.categories),
        hierarchy: dashboardFiltersStore.filters.hierarchy,
        orgIds: toJS(dashboardFiltersStore.filters.orgIds),
        groupBy: dashboardOverviewStore.groupBy,
        viewMode: dashboardOverviewStore.viewMode,
        search: dashboardOverviewStore.search,
        page: dashboardOverviewStore.page,
        pageSize: dashboardOverviewStore.pageSize,
      }),
      () => {
        if (isConsolidatedActive()) return;
        loadTargetDataOverview();
      },
      { delay: OVERVIEW_DEBOUNCE_MS },
    );

    const disposeConsolidatedOverview = reaction(
      () => ({
        years: toJS(dashboardFiltersStore.filters.years),
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        categories: toJS(dashboardFiltersStore.filters.categories),
        hierarchy: dashboardFiltersStore.filters.hierarchy,
        orgIds: toJS(dashboardFiltersStore.filters.orgIds),
        groupBy: dashboardOverviewStore.groupBy,
        viewMode: dashboardOverviewStore.viewMode,
        search: dashboardOverviewStore.search,
        page: dashboardConsolidatedMetricOverviewStore.page,
        pageSize: dashboardConsolidatedMetricOverviewStore.pageSize,
      }),
      () => {
        if (!isConsolidatedActive()) return;
        loadConsolidatedMetricOverview();
      },
      { delay: OVERVIEW_DEBOUNCE_MS },
    );

    const collapseAllDetails = () => {
      dashboardMetricDetailStore.collapseAll();
      dashboardOrgDetailStore.collapseAll();
      dashboardConsolidatedMetricDetailStore.collapseAll();
      dashboardConsolidatedOrgDetailStore.collapseAll();
    };

    const disposeCollapseOnContextChange = reaction(
      () => ({
        tab: dashboardDataTableStore.tab,
        groupBy: dashboardOverviewStore.groupBy,
        viewMode: dashboardOverviewStore.viewMode,
        page: dashboardOverviewStore.page,
        pageSize: dashboardOverviewStore.pageSize,
        consolidatedPage: dashboardConsolidatedMetricOverviewStore.page,
        consolidatedPageSize: dashboardConsolidatedMetricOverviewStore.pageSize,
      }),
      () => {
        collapseAllDetails();
      },
    );

    const disposeMetricDetail = reaction(
      () => ({
        keys: Array.from(dashboardMetricDetailStore.expandedKeys),
        panels: Array.from(dashboardMetricDetailStore.panels.entries()).map(
          ([key, p]) => ({
            key,
            search: p.search,
            statuses: toJS(p.statuses),
            drillOrgIds: toJS(p.drillOrgIds),
            page: p.page,
            pageSize: p.pageSize,
          }),
        ),
        years: toJS(dashboardFiltersStore.filters.years),
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        hierarchy: dashboardFiltersStore.filters.hierarchy,
        orgIds: toJS(dashboardFiltersStore.filters.orgIds),
      }),
      () => {
        loadAllMetricDetails();
      },
      { delay: DETAIL_DEBOUNCE_MS },
    );

    const disposeOrgDetail = reaction(
      () => ({
        keys: Array.from(dashboardOrgDetailStore.expandedKeys),
        panels: Array.from(dashboardOrgDetailStore.panels.entries()).map(
          ([key, p]) => ({
            key,
            search: p.search,
            statuses: toJS(p.statuses),
            drillYears: toJS(p.drillYears),
            drillFrameworks: toJS(p.drillFrameworks),
            drillCategories: toJS(p.drillCategories),
            page: p.page,
            pageSize: p.pageSize,
          }),
        ),
        years: toJS(dashboardFiltersStore.filters.years),
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        categories: toJS(dashboardFiltersStore.filters.categories),
        hierarchy: dashboardFiltersStore.filters.hierarchy,
        orgIds: toJS(dashboardFiltersStore.filters.orgIds),
      }),
      () => {
        loadAllOrgDetails();
      },
      { delay: DETAIL_DEBOUNCE_MS },
    );

    const disposeConsolidatedDetail = reaction(
      () => ({
        keys: Array.from(dashboardConsolidatedMetricDetailStore.expandedKeys),
        panels: Array.from(
          dashboardConsolidatedMetricDetailStore.panels.entries(),
        ).map(([key, p]) => ({
          key,
          search: p.search,
          statuses: toJS(p.statuses),
          drillOrgIds: toJS(p.drillOrgIds),
          page: p.page,
          pageSize: p.pageSize,
        })),
        years: toJS(dashboardFiltersStore.filters.years),
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        categories: toJS(dashboardFiltersStore.filters.categories),
        hierarchy: dashboardFiltersStore.filters.hierarchy,
        orgIds: toJS(dashboardFiltersStore.filters.orgIds),
      }),
      () => {
        loadAllConsolidatedMetricDetails();
      },
      { delay: DETAIL_DEBOUNCE_MS },
    );

    // Seed-and-diverge: mirror top FilterBar changes into the bottom tabs'
    // local filters (see utils/dashboard-seed.js for the mapping). Lives in
    // this effect so it exists only while the dashboard is mounted.
    // No fireImmediately: values present at mount were not a change the user
    // made up top, so the tabs keep whatever they had.
    const disposeBottomSeed = reaction(
      () => ({
        years: toJS(dashboardFiltersStore.filters.years),
        categories: toJS(dashboardFiltersStore.filters.categories),
        hierarchy: dashboardFiltersStore.filters.hierarchy,
        orgIds: toJS(dashboardFiltersStore.filters.orgIds),
      }),
      (next, prev) => seedBottomTabFilters(next, prev),
    );

    const disposeEsgDataTable = reaction(
      () => ({
        section: dashboardDataTableStore.section,
        view: dashboardDataTableStore.view,
        years: toJS(dashboardDataTableStore.filters.years),
        bus: toJS(dashboardDataTableStore.filters.bus),
        countries: toJS(dashboardDataTableStore.filters.countries),
        categories: toJS(dashboardDataTableStore.filters.categories),
        cleared: toJS(dashboardDataTableStore.cleared),
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        search: dashboardOverviewStore.search,
        page: dashboardEsgDataTableStore.page,
        pageSize: dashboardEsgDataTableStore.pageSize,
        sortBy: dashboardEsgDataTableStore.sort.by,
        sortKey: dashboardEsgDataTableStore.sort.key,
        sortDirection: dashboardEsgDataTableStore.sort.direction,
      }),
      () => {
        if (dashboardDataTableStore.section !== "esg") return;
        loadEsgDataTable();
      },
      { delay: OVERVIEW_DEBOUNCE_MS, fireImmediately: true },
    );

    const disposeConsolidatedOrgDetail = reaction(
      () => ({
        keys: Array.from(dashboardConsolidatedOrgDetailStore.expandedKeys),
        panels: Array.from(
          dashboardConsolidatedOrgDetailStore.panels.entries(),
        ).map(([key, p]) => ({
          key,
          search: p.search,
          statuses: toJS(p.statuses),
          drillFrameworks: toJS(p.drillFrameworks),
          drillCategories: toJS(p.drillCategories),
          page: p.page,
          pageSize: p.pageSize,
        })),
        years: toJS(dashboardFiltersStore.filters.years),
        frameworks: toJS(dashboardFiltersStore.filters.frameworks),
        categories: toJS(dashboardFiltersStore.filters.categories),
        hierarchy: dashboardFiltersStore.filters.hierarchy,
        orgIds: toJS(dashboardFiltersStore.filters.orgIds),
      }),
      () => {
        loadAllConsolidatedOrgDetails();
      },
      { delay: DETAIL_DEBOUNCE_MS },
    );

    return () => {
      disposeKpi();
      disposeOverview();
      disposeConsolidatedOverview();
      disposeCollapseOnContextChange();
      disposeMetricDetail();
      disposeOrgDetail();
      disposeConsolidatedDetail();
      disposeConsolidatedOrgDetail();
      disposeBottomSeed();
      disposeEsgDataTable();
    };
  }, []);

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("ESG Dashboard")}
        description={t(
          "Comparing actual performance against target setting across company hierarchy levels.",
        )}
        icon={BarChart3}
      />
      <FilterBar />
      <KpiCards />
      {user?.showCategoryDashboardWidgets && (
        <div className="grid grid-cols-1 items-stretch gap-3 xl:grid-cols-12">
          <div className="h-full xl:col-span-7">
            <CategoryProgressChart />
          </div>
          <div className="h-full xl:col-span-5">
            <CompletedTargetsChart />
          </div>
        </div>
      )}

      <TargetDataOverviewCard />
    </div>
  );
});

export default EsgDashboardPage;
